import React from 'react';
import { PageHero } from '../components/ui/PageHero';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { ServiceGroups } from '../components/ui/ServiceGroups';
import { CtaBand } from '../components/ui/CtaBand';
import { ModelSteps } from '../components/features/ModelSteps';
import { Faq } from '../components/ui/Faq';
import { IMAGES, SUPPLY_CHAIN_GROUPS } from '../data/solutions';
import { faqJsonLd, serviceJsonLd, useSeo } from '../utils/seo';
const FAQS = [{
  question: '¿Rabemar actúa como consultora o como operador logístico?',
  answer: 'Como ambos. Diseñamos la solución (red, procesos, licitaciones, KPIs) y también la ejecutamos y controlamos, con responsabilidad sobre la operación diaria.'
}, {
  question: '¿Cómo se abordan las licitaciones de transporte y de depósitos?',
  answer: 'Se releva la demanda real, se define el pliego técnico y de servicio, se evalúan proveedores con criterios objetivos y se acompaña la implementación hasta la estabilización de la operación.'
}, {
  question: '¿Qué se mide en una operación gestionada por Rabemar?',
  answer: 'KPIs y SLA definidos con el cliente: cumplimiento de entregas, ocupación de unidades, estado documental, desvíos, incidentes y costos. Los indicadores se reportan de forma periódica.'
}];
const CRUMBS = [{
  name: 'Inicio',
  path: '/'
}, {
  name: 'Soluciones',
  path: '/soluciones'
}, {
  name: 'Logística & Supply Chain',
  path: '/soluciones/logistica-supply-chain'
}];
export function LogisticsSupplyChain() {
  useSeo({
    title: 'Logística & Supply Chain en Argentina | Consultoría y operación — Rabemar',
    description: 'Consultoría de Supply Chain, diseño de cadenas de suministro, análisis de redes de transporte, licitaciones, warehousing, KPIs y excelencia operacional en Argentina.',
    path: '/soluciones/logistica-supply-chain',
    image: IMAGES.warehouse,
    breadcrumbs: CRUMBS,
    jsonLd: [serviceJsonLd('Consultoría y operación de Supply Chain', 'Diseño de cadenas de suministro, análisis de redes de transporte, licitaciones de transporte y depósitos, warehousing, KPIs y mejora continua.', '/soluciones/logistica-supply-chain'), faqJsonLd(FAQS)]
  });
  return <>
      <PageHero eyebrow="01 — Logística & Supply Chain" title="Diseñamos, implementamos y controlamos operaciones logísticas de punta a punta." lead="Socio estratégico y operativo: desde el análisis de la red y las licitaciones hasta la ejecución diaria, la medición de KPIs y la mejora continua." image={IMAGES.warehouse} imageAlt="Operario con casco y chaleco reflectante moviendo un pallet con autoelevador en un depósito moderno" crumbs={CRUMBS} ctaLabel="Analizar mi operación logística" ctaService="Logística" stats={[{
      value: 'Punta a punta',
      label: 'Diseño, ejecución y control'
    }, {
      value: 'KPIs + SLA',
      label: 'Indicadores definidos con el cliente'
    }, {
      value: 'Warehousing',
      label: 'Soluciones de almacenaje y autoelevadores'
    }, {
      value: 'Argentina',
      label: 'Cobertura nacional'
    }]} data-id="element-764" data-mp="3fnyt6dymau6c" />

      <section className="bg-white py-20 md:py-28" data-id="element-765" data-mp="cb636o8k274w">
        <Container data-id="element-766" data-mp="7dwg3f8n27k8">
          <div className="max-w-3xl" data-id="element-767" data-mp="7efs6fbly1qc">
            <Eyebrow data-id="element-768" data-mp="7dea3qw6cez4">Alcance de servicios</Eyebrow>
            <Heading as="h2" size="md" className="mt-5" data-id="element-769" data-mp="2etclz8b49ed">
              Cuatro frentes que se trabajan en simultáneo.
            </Heading>
            <p className="mt-5 text-lg leading-relaxed text-graphite-500" data-id="element-770" data-mp="3trxr7vcja89a">
              La operación no se estabiliza sólo con estrategia ni sólo con ejecución. Rabemar cubre el ciclo completo:
              analiza, licita, implementa, mide y corrige.
            </p>
          </div>
          <div className="mt-14" data-id="element-771" data-mp="3fs4v1ki8mztx">
            <ServiceGroups groups={SUPPLY_CHAIN_GROUPS} data-id="element-772" data-mp="3fvajcxj1lxol" />
          </div>
        </Container>
      </section>

      <section className="bg-mist-50 py-20 md:py-28" data-id="element-773" data-mp="1er8aj7iqyre4">
        <Container data-id="element-774" data-mp="1esjhthbeih9z">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16" data-id="element-775" data-mp="1etbbgmlfu4xm">
            <div data-id="element-776" data-mp="1jp05kaeoutmj">
              <Eyebrow data-id="element-777" data-mp="1jpjonug2ro25">Excelencia operacional</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-778" data-mp="1526uxig46c0o">
                Optimizar cuesta menos que repetir errores.
              </Heading>
            </div>
            <div className="grid gap-px bg-mist-200 sm:grid-cols-2" data-id="element-779" data-mp="19y5fsw28hbwc">
              {[{
              title: 'Optimización de costos',
              text: 'Análisis de la estructura de costos logísticos, detección de duplicidades y rediseño de tramos y frecuencias.'
            }, {
              title: 'Evaluación de proveedores',
              text: 'Criterios objetivos de habilitación, desempeño y cumplimiento documental para cada proveedor y subcontratista.'
            }, {
              title: 'Procesos y procedimientos',
              text: 'Documentación de la operación para que el resultado no dependa de personas puntuales.'
            }, {
              title: 'Mejora continua',
              text: 'Revisión periódica de indicadores con planes de acción concretos y responsables asignados.'
            }].map(item => <div key={item.title} className="bg-white p-7" data-id="element-780" data-mp="eq3j1ur68doa">
                  <h3 className="font-display text-lg font-bold tracking-tight text-graphite-900" data-id="element-781" data-mp="shrsfqa1co1k">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-graphite-500" data-id="element-782" data-mp="ogg03czs1opo">{item.text}</p>
                </div>)}
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-white py-20 md:py-28" data-id="element-783" data-mp="xj7dhic7qujy">
        <Container data-id="element-784" data-mp="3483vh3z5796d">
          <div className="max-w-3xl" data-id="element-785" data-mp="347kd5m5kyirt">
            <Eyebrow data-id="element-786" data-mp="348m1gsblx1rh">Modelo Rabemar</Eyebrow>
            <Heading as="h2" size="md" className="mt-5" data-id="element-787" data-mp="2z9iwf7vmmvji">
              Del diagnóstico a la optimización.
            </Heading>
          </div>
          <ModelSteps data-id="element-788" data-mp="137p6flehpaxf" />
        </Container>
      </section>

      <section className="bg-mist-50 py-20 md:py-28" data-id="element-789" data-mp="184pc3v7fgbo0">
        <Container data-id="element-790" data-mp="185h7b4x447dr">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16" data-id="element-791" data-mp="1c6sr2ttb1ye1">
            <div data-id="element-792" data-mp="1c8x4q3h0abri">
              <Eyebrow data-id="element-793" data-mp="1h5vxahl0uanl">Preguntas frecuentes</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-794" data-mp="1h6otwpq9njs0">
                Logística & Supply Chain
              </Heading>
            </div>
            <Faq items={FAQS} data-id="element-795" data-mp="1m35syez07ijv" />
          </div>
        </Container>
      </section>

      <CtaBand title="Analicemos su operación logística." text="Relevamos red, costos, proveedores y niveles de servicio, y devolvemos un alcance concreto con indicadores medibles." primaryLabel="Analizar mi operación logística" location="logistica-cta" service="Logística" data-id="element-796" data-mp="1r28mp39ja2cj" />
    </>;
}